/**
 * Recursos das Automações (Touch Builder, editor de fluxos, blocos, agente de IA) e da Base de
 * Conhecimento que o ponteiro de uma novidade sabe apontar. Ver `spotlightTargets.js`.
 *
 * A Base de Conhecimento não tem item de menu lateral (fica no menu do avatar), então seus
 * recursos moram aqui, com o cartão ancorado em Automações: é a tela que o agente de IA usa.
 *
 * O editor de fluxo só existe depois de abrir um fluxo da lista, e o painel de propriedades só
 * depois de clicar num bloco; por isso quase tudo daqui tem roteiro (`steps`). O editor não tem
 * trava de rota: o `minLevel` 50 dos recursos dele impede o anel para quem chegou pela URL.
 *
 * Limites do ponteiro no quadro: os blocos são movidos pelo ReactFlow (o anel acompanha, mas
 * treme enquanto a pessoa arrasta), e um bloco fora da área visível do quadro continua no DOM,
 * então o anel aponta para fora da tela até a pessoa enquadrar o fluxo.
 */

const HUB = "Touch Builder: lista de fluxos";
const EDITOR = "Editor de fluxo";
const BIBLIOTECA = "Biblioteca de blocos";
const BLOCOS = "Blocos no quadro";
const AGENTE = "Painel do Agente IA";
const BASE = "Base de Conhecimento";

const ABRIR_FLUXO = { key: "automation.hub-flow-card", text: "Abra um fluxo clicando no cartão" };
const ABRIR_BIBLIOTECA = { key: "automation.editor-palette-toggle", text: "Abra a biblioteca de blocos" };
const stepsDoEditor = (what) => [{ ...ABRIR_FLUXO, text: `${ABRIR_FLUXO.text} para ver ${what}.` }];
const stepsDoBloco = (slug, name) => [
  { ...ABRIR_FLUXO, text: `${ABRIR_FLUXO.text}.` },
  { ...ABRIR_BIBLIOTECA, text: `${ABRIR_BIBLIOTECA.text} e arraste o bloco ${name} para o quadro.` },
  { key: `automation.palette-${slug}`, text: `Arraste o bloco ${name} para o quadro.` },
];
const stepsDoAgente = (what) => [
  { ...ABRIR_FLUXO, text: `${ABRIR_FLUXO.text}.` },
  { ...ABRIR_BIBLIOTECA, text: `${ABRIR_BIBLIOTECA.text} e arraste o bloco Agente IA para o quadro.` },
  { key: "automation.palette-ai-agent", text: "Arraste o bloco Agente IA para o quadro." },
  { key: "automation.node-ai-agent", text: `Clique no bloco Agente IA para ver ${what}.` },
];

/** Um bloco da biblioteca e o mesmo bloco já no quadro: dois recursos por tipo. */
const bloco = (type, label, hint) => {
  const slug = type.replace(/_/g, "-");
  return [
    {
      key: `automation.palette-${slug}`,
      navKey: "automation",
      group: BIBLIOTECA,
      label: `${label} (na biblioteca)`,
      path: "/automation",
      requires: "panel",
      minLevel: 50,
      hint: `${hint} Aponta a linha do bloco na biblioteca, que precisa estar aberta.`,
      steps: [
        { ...ABRIR_FLUXO, text: `${ABRIR_FLUXO.text}.` },
        { ...ABRIR_BIBLIOTECA, text: `${ABRIR_BIBLIOTECA.text} e arraste o bloco ${label} para o quadro.` },
      ],
    },
    {
      key: `automation.node-${slug}`,
      navKey: "automation",
      group: BLOCOS,
      label: `${label} (no quadro)`,
      path: "/automation",
      requires: "panel",
      minLevel: 50,
      hint: `${hint} Aponta o bloco já colocado no quadro de um fluxo.`,
      steps: stepsDoBloco(slug, label),
    },
  ];
};

export const AUTOMATION_TARGETS = [
  // ── Lista de fluxos ─────────────────────────────────────────────────────
  {
    key: "automation.hub-new-flow",
    navKey: "automation",
    group: HUB,
    label: "Botão Novo Fluxo",
    path: "/automation",
    requires: "page",
    minLevel: 50,
    hint: "Cria uma automação escolhendo o gatilho (WhatsApp, webhook, sistema jurídico) ou um modelo pronto.",
  },
  {
    key: "automation.hub-import-flow",
    navKey: "automation",
    group: HUB,
    label: "Botão Importar",
    path: "/automation",
    requires: "page",
    minLevel: 50,
    hint: "Traz um fluxo exportado de outra conta; ele entra como rascunho para revisar e publicar.",
  },
  {
    key: "automation.hub-search-flows",
    navKey: "automation",
    group: HUB,
    label: "Busca de fluxos",
    path: "/automation",
    requires: "page",
    minLevel: 50,
    hint: "Encontra uma automação pelo nome.",
  },
  {
    key: "automation.hub-flow-card",
    navKey: "automation",
    group: HUB,
    label: "Cartão de um fluxo",
    path: "/automation",
    requires: "data",
    minLevel: 50,
    hint: "O cartão de cada fluxo da conta: clicar abre o editor. Só existe com ao menos um fluxo criado.",
  },

  // ── Editor de fluxo ─────────────────────────────────────────────────────
  {
    key: "automation.editor-canvas",
    navKey: "automation",
    group: EDITOR,
    label: "Quadro do fluxo",
    path: "/automation",
    requires: "panel",
    minLevel: 50,
    hint: "A área onde os blocos são soltos e ligados uns aos outros para montar o atendimento.",
    steps: stepsDoEditor("o quadro"),
  },
  {
    key: "automation.editor-palette-toggle",
    navKey: "automation",
    group: EDITOR,
    label: "Botão da biblioteca de blocos",
    path: "/automation",
    requires: "panel",
    minLevel: 50,
    hint: "O botão roxo no canto do quadro abre a biblioteca com todos os blocos. Some enquanto a biblioteca está aberta.",
    steps: stepsDoEditor("o botão da biblioteca"),
  },
  {
    key: "automation.editor-palette-drawer",
    navKey: "automation",
    group: EDITOR,
    label: "Biblioteca de blocos aberta",
    path: "/automation",
    requires: "panel",
    minLevel: 50,
    hint: "A lista de blocos com busca, para arrastar mensagens, condições, IA e integrações. Só com a biblioteca aberta.",
    steps: [
      { ...ABRIR_FLUXO, text: `${ABRIR_FLUXO.text}.` },
      { ...ABRIR_BIBLIOTECA, text: `${ABRIR_BIBLIOTECA.text}.` },
    ],
  },
  {
    key: "automation.editor-test",
    navKey: "automation",
    group: EDITOR,
    label: "Botão Testar",
    path: "/automation",
    requires: "panel",
    minLevel: 50,
    hint: "Conversa com o fluxo num celular simulado antes de publicar.",
    steps: stepsDoEditor("o botão Testar"),
  },
  {
    key: "automation.editor-save",
    navKey: "automation",
    group: EDITOR,
    label: "Botão Salvar",
    path: "/automation",
    requires: "panel",
    minLevel: 50,
    hint: "Guarda o rascunho; o que está no ar só muda quando a pessoa publica.",
    steps: stepsDoEditor("o botão Salvar"),
  },
  {
    key: "automation.editor-publish",
    navKey: "automation",
    group: EDITOR,
    label: "Botão Publicar",
    path: "/automation",
    requires: "panel",
    minLevel: 50,
    hint: "Coloca as alterações no ar; até publicar, vale a versão anterior.",
    steps: stepsDoEditor("o botão Publicar"),
  },
  {
    key: "automation.editor-unpublished-badge",
    navKey: "automation",
    group: EDITOR,
    label: "Selo Alterações não publicadas",
    path: "/automation",
    requires: "panel",
    minLevel: 50,
    hint: "Avisa que há rascunho salvo ainda fora do ar. Só aparece depois de salvar uma alteração e antes de publicar.",
    steps: stepsDoEditor("o editor"),
  },
  {
    key: "automation.editor-history",
    navKey: "automation",
    group: EDITOR,
    label: "Histórico de versões",
    path: "/automation",
    requires: "panel",
    minLevel: 50,
    hint: "O ícone de relógio volta a uma versão publicada anterior se algo der errado.",
    steps: stepsDoEditor("o histórico de versões"),
  },
  {
    key: "automation.editor-logs-toggle",
    navKey: "automation",
    group: EDITOR,
    label: "Chave Logs",
    path: "/automation",
    requires: "panel",
    minLevel: 50,
    hint: "Liga o painel das conversas que passaram pelo fluxo, com o caminho que cada uma percorreu.",
    steps: stepsDoEditor("a chave Logs"),
  },
  {
    key: "automation.editor-validation-panel",
    navKey: "automation",
    group: EDITOR,
    label: "Erros e avisos do fluxo",
    path: "/automation",
    requires: "panel",
    minLevel: 50,
    hint: "Mostra o que impede o fluxo de funcionar e leva até o bloco. Só aparece quando a validação encontra algo (ao salvar ou publicar).",
    steps: stepsDoEditor("o editor"),
  },
  {
    key: "automation.editor-properties-panel",
    navKey: "automation",
    group: EDITOR,
    label: "Painel de propriedades do bloco",
    path: "/automation",
    requires: "panel",
    minLevel: 50,
    hint: "A gaveta da direita com a configuração do bloco selecionado. Só depois de clicar num bloco.",
    steps: [
      { ...ABRIR_FLUXO, text: `${ABRIR_FLUXO.text}.` },
      { key: "automation.editor-canvas", text: "Clique num bloco do quadro para configurá-lo." },
    ],
  },

  // ── Blocos (biblioteca e quadro) ────────────────────────────────────────
  ...bloco("start", "Início", "Liga o canal de WhatsApp que inicia o fluxo."),
  ...bloco("webhook_start", "Webhook Trigger", "Recebe dados de outro sistema por uma URL e dispara o fluxo."),
  ...bloco("cron_trigger", "Trigger Agendado", "Roda o fluxo sozinho em dias e horários fixos."),
  ...bloco("condition", "Condição (If/Else)", "Segue por caminhos diferentes conforme uma resposta ou dado."),
  ...bloco("ai_agent", "Agente IA", "Um atendente de IA que conversa, consulta dados e transfere."),
  ...bloco("classifier", "Classificador", "Descobre a intenção do cliente e encaminha por categoria."),
  ...bloco("whatsapp", "Enviar WhatsApp", "A mensagem que o cliente recebe; botões criam ramificações."),
  ...bloco("transfer", "Transferência", "Passa a conversa para uma pessoa ou departamento, com resumo automático."),
  ...bloco("mcp", "MCP (Ferramentas)", "Conecta ferramentas externas para os agentes de IA executarem ações reais."),
  ...bloco("fetch", "Requisição HTTP", "Chama uma API externa e usa a resposta no fluxo."),
  ...bloco("survey", "Pesquisa (NPS/CSAT)", "Pede a nota do cliente e ramifica por satisfeito ou insatisfeito."),
  ...bloco("crm", "CRM", "Cria e move negócios no Kanban sem sair do fluxo."),

  // ── Painel do Agente IA ─────────────────────────────────────────────────
  {
    key: "automation.agent-instructions",
    navKey: "automation",
    group: AGENTE,
    label: "Instruções do agente",
    path: "/automation",
    requires: "panel",
    minLevel: 50,
    hint: "O texto que define quem é o agente, o tom e as regras: é onde mora o comportamento dele.",
    steps: stepsDoAgente("as instruções"),
  },
  {
    key: "automation.agent-model",
    navKey: "automation",
    group: AGENTE,
    label: "Modelo e raciocínio",
    path: "/automation",
    requires: "panel",
    minLevel: 50,
    hint: "Escolhe o modelo do agente; os modelos Claude são premium e aceitam chave própria.",
    steps: stepsDoAgente("o modelo"),
  },
  {
    key: "automation.agent-rag",
    navKey: "automation",
    group: AGENTE,
    label: "Base de Conhecimento (RAG) do agente",
    path: "/automation",
    requires: "panel",
    minLevel: 50,
    hint: "Liga a consulta aos documentos da Base de Conhecimento antes de responder.",
    steps: stepsDoAgente("a seção da Base de Conhecimento"),
  },
  {
    key: "automation.agent-response-settings",
    navKey: "automation",
    group: AGENTE,
    label: "Configurações de resposta",
    path: "/automation",
    requires: "panel",
    minLevel: 50,
    hint: "Define se o agente responde por texto ou áudio e se usa botões do WhatsApp.",
    steps: stepsDoAgente("as configurações de resposta"),
  },
  {
    key: "automation.agent-timeout",
    navKey: "automation",
    group: AGENTE,
    label: "Timeout de inatividade",
    path: "/automation",
    requires: "panel",
    minLevel: 50,
    hint: "Decide o que fazer quando o cliente para de responder. Some enquanto o Follow Up está ligado.",
    steps: stepsDoAgente("o timeout"),
  },
  {
    key: "automation.agent-followup",
    navKey: "automation",
    group: AGENTE,
    label: "Follow Up",
    path: "/automation",
    requires: "panel",
    minLevel: 50,
    hint: "Reengaja quem parou de responder com lembretes nos tempos definidos.",
    steps: stepsDoAgente("o Follow Up"),
  },
  {
    key: "automation.agent-memory",
    navKey: "automation",
    group: AGENTE,
    label: "Memória de conversa",
    path: "/automation",
    requires: "panel",
    minLevel: 50,
    hint: "Faz o agente lembrar do que foi dito em conversas anteriores.",
    steps: stepsDoAgente("a memória"),
  },
  {
    key: "automation.agent-handoffs",
    navKey: "automation",
    group: AGENTE,
    label: "Handoffs entre agentes",
    path: "/automation",
    requires: "panel",
    minLevel: 50,
    hint: "Cria especialistas (jurídico, comercial) e deixa o agente passar a conversa entre eles. Só com o modo conversação ligado.",
    steps: stepsDoAgente("os handoffs"),
  },
  {
    key: "automation.agent-tools",
    navKey: "automation",
    group: AGENTE,
    label: "Ferramentas do agente",
    path: "/automation",
    requires: "panel",
    minLevel: 50,
    hint: "Escolhe o que o agente pode usar (agenda, e-mail, CRM, sistemas jurídicos) e a mensagem de espera.",
    steps: stepsDoAgente("as ferramentas"),
  },
  {
    key: "automation.agent-output-variable",
    navKey: "automation",
    group: AGENTE,
    label: "Variável de saída e modo conversação",
    path: "/automation",
    requires: "panel",
    minLevel: 50,
    hint: "O nome da variável que guarda a resposta do agente e a chave que decide se ele conversa várias vezes.",
    steps: stepsDoAgente("a variável de saída"),
  },

  // ── Base de Conhecimento ────────────────────────────────────────────────
  {
    key: "automation.kb-add-file",
    navKey: "automation",
    group: BASE,
    label: "Botão Adicionar Arquivo",
    path: "/knowledge-base",
    requires: "page",
    feature: "rag.enabled",
    minLevel: 10,
    hint: "Envia PDF, DOCX, TXT ou CSV (até 16 MB) para a IA consultar ao responder.",
  },
  {
    key: "automation.kb-documents-table",
    navKey: "automation",
    group: BASE,
    label: "Lista de arquivos da base",
    path: "/knowledge-base",
    requires: "page",
    feature: "rag.enabled",
    minLevel: 10,
    hint: "Os arquivos que a IA usa como contexto, com data e tamanho; dá para ordenar e excluir.",
  },
  {
    key: "automation.kb-search",
    navKey: "automation",
    group: BASE,
    label: "Busca de arquivos",
    path: "/knowledge-base",
    requires: "page",
    feature: "rag.enabled",
    minLevel: 10,
    hint: "Filtra a lista pelo nome do arquivo (não busca dentro do conteúdo).",
  },
  {
    key: "automation.kb-refresh",
    navKey: "automation",
    group: BASE,
    label: "Atualizar a lista",
    path: "/knowledge-base",
    requires: "page",
    feature: "rag.enabled",
    minLevel: 10,
    hint: "O ícone de setas recarrega a lista depois de enviar ou excluir um arquivo.",
  },
];
